import type { SuggestedChip } from '@/components/ui/transcript-chips/types';
import {
  GET_EVIDENCE_DOCUMENT_SUGGESTION_EXPERIMENT_VARIANT,
  IS_CLINICAL_TASKS_ENABLED,
} from '@/features/flags/legacy-flags';
import {
  DOCUMENT_SUGGESTION_BANNER_MAX_ITEMS,
  documentSuggestionPayloadToSuggestedChip,
  filterDocumentCreationSuggestions,
  type DocumentSuggestionPayload,
} from '@/helpers/clinical-tasks/documentCreationTask';
import { useManualExposure } from '@/hooks/amplitude/use-manual-exposure';
import { useMutateUpdateSessionClinicalTask } from '@/hooks/mutation/useMutateUpdateSessionClinicalTask';
import { useQueryClinicalTasksBySessionId } from '@/hooks/query/useQueryClinicalTasksBySessionId';
import { useClinicalTasksPanelAvailability } from '@/hooks/useClinicalTasksPanelAvailability';
import {
  DOCUMENT_SUGGESTION_BANNER_COOLDOWN_DAYS,
  useDocumentSuggestionBannerSuppression,
  type SuppressionTriggerReason,
} from '@/hooks/useDocumentSuggestionBannerSuppression';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import { ClinicalTaskStatus } from '@heidi/schema/ml-scribe';
import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Derives document-drafting suggestions from session clinical tasks for the in-session banner
 * (`banner` variant) or suggestion chips in the peek sheet (`chips` variant).
 */
type UseDocumentCreationSuggestionBannerOptions = {
  /**
   * When false, skips shown-state analytics and `recordShown` so a
   * visibility-only caller (e.g. coexistence gating) does not double-count.
   */
  trackImpressions?: boolean;
};

export function useDocumentCreationSuggestionBanner(
  sessionId: string | undefined,
  { trackImpressions = true }: UseDocumentCreationSuggestionBannerOptions = {}
) {
  const variant = GET_EVIDENCE_DOCUMENT_SUGGESTION_EXPERIMENT_VARIANT();
  const sid = sessionId ?? '';
  const { shouldShowClinicalTasksPanel } = useClinicalTasksPanelAvailability(sid);
  const { data, isLoading } = useQueryClinicalTasksBySessionId(sid);
  const { mutate: mutateUpdateSessionClinicalTask } =
    useMutateUpdateSessionClinicalTask();

  // Fire the analytics event for any suppression trigger from a single place so
  // every entry path (auto via dismissal, auto via abandonment, manual button) emits
  // a consistent shape. `manual` is permanent — `cooldown_days: null` flags that.
  const handleCooldownTriggered = useCallback(
    (reason: SuppressionTriggerReason) => {
      if (!sessionId) return;
      track({
        event_name: TrackingEventName.DocumentSuggestionBannerSuppressed,
        props: {
          session_id: sessionId,
          reason,
          cooldown_days:
            reason === 'manual' ? null : DOCUMENT_SUGGESTION_BANNER_COOLDOWN_DAYS,
        },
      });
    },
    [sessionId]
  );

  const {
    isSuppressed,
    isLoading: isSuppressionLoading,
    recordShown,
    recordEngaged,
    recordAllDismissed,
    recordManualHide,
  } = useDocumentSuggestionBannerSuppression(sessionId, {
    onCooldownTriggered: handleCooldownTriggered,
  });

  const baseEnabled =
    IS_CLINICAL_TASKS_ENABLED() && Boolean(sessionId) && shouldShowClinicalTasksPanel;

  useManualExposure({
    flagKey: 'exp-evidence-document-suggestion',
    isEnabled: variant !== 'control',
    isVisible: trackImpressions && !!baseEnabled,
    variant,
  });

  const bannerEnabled = variant === 'banner' && baseEnabled && !isSuppressed;
  // The suppression behaviour is banner-specific. The chips variant has its own
  // dismissal UX in the peek sheet and shouldn't be hidden by this counter.
  const chipsVariantEnabled = variant === 'chips' && baseEnabled;

  const suggestions: DocumentSuggestionPayload[] = useMemo(() => {
    if (!(bannerEnabled || chipsVariantEnabled) || !data?.tasks?.length) {
      return [];
    }
    const todos = data.tasks.filter((t) => t.status === ClinicalTaskStatus.todo);
    return filterDocumentCreationSuggestions(todos, DOCUMENT_SUGGESTION_BANNER_MAX_ITEMS);
  }, [bannerEnabled, chipsVariantEnabled, data?.tasks]);

  const documentSuggestionChips: SuggestedChip[] = useMemo(() => {
    if (!chipsVariantEnabled || isLoading || suggestions.length === 0) {
      return [];
    }
    return suggestions.map(documentSuggestionPayloadToSuggestedChip);
  }, [chipsVariantEnabled, isLoading, suggestions]);

  // Hide the banner until both the suggestions data and the suppression view
  // have hydrated — otherwise we briefly show the banner before the user query
  // hydrates and would then re-hide it (visible flash).
  const shouldShowBanner = Boolean(
    bannerEnabled && suggestions.length > 0 && !isLoading && !isSuppressionLoading
  );

  // True if either the banner or the chips variant is currently visible.
  const isShowing = shouldShowBanner || documentSuggestionChips.length > 0;

  const shownForSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionId) {
      shownForSessionRef.current = null;
      return;
    }
    if (sessionId !== shownForSessionRef.current) {
      shownForSessionRef.current = null;
    }
  }, [sessionId]);

  useEffect(() => {
    if (!trackImpressions) return;
    if (!shouldShowBanner || !sessionId) {
      return;
    }
    if (shownForSessionRef.current === sessionId) {
      return;
    }
    shownForSessionRef.current = sessionId;
    recordShown(sessionId);
    track({
      event_name: TrackingEventName.DocumentSuggestionBannerShown,
      props: {
        session_id: sessionId,
        suggestions_count: suggestions.length,
        has_carousel: suggestions.length > 1,
        suggestion_categories: suggestions.map((s) => s?.category),
      },
    });
    // Emit once per session — shownForSessionRef guards against re-emission.
  }, [trackImpressions, shouldShowBanner, sessionId, suggestions, recordShown]);

  const dismissSuggestionByTaskId = useCallback(
    (taskId: string) => {
      if (!sessionId) return;
      const task = data?.tasks?.find((t) => t.id === taskId);
      if (!task || task.is_suggestion_dismissed) return;

      const remainingAfter = suggestions.filter((s) => s.taskId !== taskId).length;

      mutateUpdateSessionClinicalTask({
        taskId,
        sessionId,
        taskData: { is_suggestion_dismissed: true },
      });

      track({
        event_name: TrackingEventName.DocumentSuggestionBannerDismissed,
        props: {
          session_id: sessionId,
          suggestions_count: remainingAfter,
          task_id: taskId,
        },
      });

      // Last X click in this session — count it toward the auto-suppression streak.
      if (remainingAfter === 0) {
        recordAllDismissed(sessionId);
      }
    },
    [
      data?.tasks,
      mutateUpdateSessionClinicalTask,
      sessionId,
      suggestions,
      recordAllDismissed,
    ]
  );

  const hideAllSuggestions = useCallback(
    (menuLabel: string) => {
      if (!sessionId) return;
      track({
        event_name: TrackingEventName.ButtonClicked,
        props: {
          action: ButtonAction.HideDocumentSuggestions,
          label: menuLabel,
          feature: 'document_suggestion_banner',
          session_id: sessionId,
          suggestion_count: suggestions.length,
        },
      });
      recordManualHide(sessionId);
    },
    [recordManualHide, sessionId, suggestions.length]
  );

  const trackCarouselNav = useCallback(
    (direction: 'next' | 'prev', index: number) => {
      if (!sessionId) return;
      track({
        event_name: TrackingEventName.DocumentSuggestionBannerCarouselNavigated,
        props: {
          session_id: sessionId,
          direction,
          index,
          suggestions_count: suggestions.length,
        },
      });
    },
    [sessionId, suggestions.length]
  );

  const trackCta = useCallback(
    (item: DocumentSuggestionPayload) => {
      if (!sessionId) return;
      // Engagement clears any pending non-engagement streak.
      recordEngaged(sessionId);
      track({
        event_name: TrackingEventName.DocumentSuggestionBannerCtaClicked,
        props: {
          session_id: sessionId,
          task_id: item.taskId,
          cta: item.cta,
          suggestions_count: suggestions.length,
        },
      });
    },
    [sessionId, suggestions.length, recordEngaged]
  );

  return useMemo(
    () => ({
      shouldShowBanner,
      isShowing,
      suggestions,
      documentSuggestionChips,
      dismissSuggestionByTaskId,
      trackCarouselNav,
      trackCta,
      hideAllSuggestions,
    }),
    [
      shouldShowBanner,
      isShowing,
      suggestions,
      documentSuggestionChips,
      dismissSuggestionByTaskId,
      trackCarouselNav,
      trackCta,
      hideAllSuggestions,
    ]
  );
}
